// @ts-strict-ignore
import { useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';

/**
 * Use FFMPEG (v4.4) to create the WEBM video. Didn´t create an mp4 since it does not support transparency.
 *
 * > ffmpeg -i spinner.gif -c vp9 -b:v 0 -crf 41 spinner.webm
 *
 */
import { CssSymbol } from '../icons/CssSymbol.tsx';
import spinnerWebm from './spinner.webm';
import spinnerGif from './spinner.gif';
/**
 * This new CssSymbol is almost identical to the gif and webm animations, but is not as heavy (about 100kb for movie and 300kb for gif) vs simple html+css
 * It is enabled by default unless preferWebmOrGif=true
 */

interface LoadingIndicatorProps {
    delay?: number;
    centered?: boolean;
    preferWebmOrGif?: boolean;
    /**
     * either a named size, or an absolute size in pixels.
     */
    size?: 'small' | 'medium' | 'large' | number | string;
    className?: string;
    testId?: string;
}

// Map size names to pixel sizes
const sizeMap = {
    small: 32,
    medium: 64,
    large: 128,
};

const Indicator = styled('video', {
    shouldForwardProp: (propName: string) =>
        !['size', 'centered', 'visible', 'gif'].includes(propName),
})<{
    size: number | string;
    centered: boolean;
    visible: boolean;
    gif?: true;
    css?: true;
}>(({ size, visible, centered, gif }) => ({
    width: size,
    height: size,
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.5s ease-in',
    ...(centered
        ? {
              display: 'block',
              margin: 'auto',
          }
        : undefined),
    ...(gif
        ? {
              background: `url(${spinnerGif}) no-repeat center bottom`,
              backgroundSize: `100% 100%`,
          }
        : undefined),
}));
/**
 * By passing children, the loading indicator will be displayed as an overlay
 */
export const LoadingIndicator = (props: LoadingIndicatorProps) => {
    const {
        delay = 500,
        size = 'large',
        centered = false,
        preferWebmOrGif = false,
        testId,
        className,
    } = props;
    const [visible, setVisible] = useState(delay <= 0);
    const [fallbackToGif, setFallbackToGif] = useState(false);
    useEffect(() => {
        const timeoutId =
            delay > 0 && setTimeout(() => setVisible(true), delay);
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [delay]);

    const pixelSize = sizeMap[size] || size;
    return (
        <Box
            sx={centered ? { display: 'flex', height: '100%' } : undefined}
            className={className}
        >
            {fallbackToGif && preferWebmOrGif ? (
                <Indicator
                    as="span"
                    gif
                    centered={centered}
                    visible={visible}
                    size={pixelSize}
                    className={props.className}
                    data-testid={testId}
                />
            ) : preferWebmOrGif ? (
                <Indicator
                    centered={centered}
                    visible={visible}
                    size={pixelSize}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={props.className}
                    data-testid={testId}
                    onError={() => {
                        // No webm support, lets show the gif
                        //
                        // According to caniuse (https://caniuse.com/webm) only
                        // internet explorer and opera mini has no support. There
                        // are some issues in safari as well with webm on older
                        // operative systems and on mobile safari. This will likely
                        // improve over time.
                        //
                        // See https://stackoverflow.com/questions/31330780/use-gifs-in-video-tag-or-use-videos-in-img-tags
                        // for info on gif and <video/>. Using the poster attribute is an option, but we cannot set it
                        // until the error is caught, so might as well remove the video element in case of error and
                        // show/load the gif.
                        setFallbackToGif(true);
                    }}
                >
                    <source src={spinnerWebm} type="video/webm" />
                    {/* Fallback to <img src={spinnerGif} /> did not yield a result */}
                    {/* Fallback to mp4 is not relevant since it does not support transparency */}
                </Indicator>
            ) : (
                <CssSymbol
                    animated
                    size={pixelSize}
                    sx={{
                        opacity: visible ? 1 : 0,
                        transition: 'opacity 0.5s ease-in',
                        ...(centered
                            ? {
                                  margin: 'auto',
                              }
                            : undefined),
                    }}
                />
            )}
        </Box>
    );
};

export default LoadingIndicator;
